import { Link, useParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import ScrollToTopButton from '@/components/feature/ScrollToTopButton';
import Breadcrumbs from '@/components/base/Breadcrumbs';
import MomentCard from '@/components/base/MomentCard';
import { useSeo } from '@/hooks/useSeo';
import { moments } from '@/mocks/moments';
import { siteInfo } from '@/mocks/site';
import { formatCount, formatDate } from '@/utils/format';

export default function MomentDetail() {
  const { id = '' } = useParams();
  const moment = moments.find((item) => item.id === id);
  const related = moment
    ? moments.filter((item) => item.category === moment.category && item.id !== moment.id).slice(0, 3)
    : [];

  useSeo({
    title: moment ? `${moment.title}｜私拍吃瓜名场面拆解 - ${siteInfo.name}` : `名场面未找到 - ${siteInfo.name}`,
    description: moment
      ? `${moment.summary} 已标注 ${moment.highlights.length} 处关键画面节点,可直接定位最值得看的瞬间。`
      : '抱歉,这条名场面暂时不存在,请返回图鉴浏览其它内容。',
    keywords: moment ? moment.tags.join(',') : '私拍吃瓜名场面',
    path: `/moment/${id}`,
    jsonLd: moment
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: moment.title,
            description: moment.summary,
            image: moment.cover,
            datePublished: moment.date,
            articleSection: moment.category,
            keywords: moment.tags.join(','),
            inLanguage: 'zh-CN',
            author: { '@type': 'Organization', name: siteInfo.name },
            publisher: { '@type': 'Organization', name: siteInfo.name },
            mainEntityOfPage: `${siteInfo.domain}/moment/${moment.id}`,
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: siteInfo.domain },
              { '@type': 'ListItem', position: 2, name: '名场面图鉴', item: `${siteInfo.domain}/moments` },
              {
                '@type': 'ListItem',
                position: 3,
                name: moment.title,
                item: `${siteInfo.domain}/moment/${moment.id}`,
              },
            ],
          },
        ]
      : undefined,
  });

  if (!moment) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <SiteHeader />
        <main className="flex-1 pt-16 md:pt-20 flex items-center justify-center px-4 py-24">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-emotion-unhappy-line text-3xl"></i>
            </div>
            <h1 className="mt-6 font-heading text-2xl text-foreground-950">这条名场面没找到</h1>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              可能是链接不对,或者这条内容已经被调整。回到图鉴重新挑一条吧。
            </p>
            <Link
              to="/moments"
              className="mt-6 inline-flex px-5 py-2.5 rounded-md text-sm whitespace-nowrap bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
            >
              返回名场面图鉴
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1 pt-16 md:pt-20">
        <div className="px-4 md:px-8 pt-8 md:pt-10">
          <Breadcrumbs
            items={[
              { label: '首页', to: '/' },
              { label: '名场面图鉴', to: '/moments' },
              { label: moment.category, to: `/moments?category=${encodeURIComponent(moment.category)}` },
              { label: moment.title },
            ]}
          />
        </div>

        <article className="px-4 md:px-8 py-8 md:py-10">
          <header className="max-w-4xl">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs bg-accent-100 text-accent-900">
              <i className="ri-price-tag-3-line"></i>
              {moment.category}
            </span>
            <h1 className="mt-4 font-heading text-3xl md:text-5xl leading-tight text-foreground-950">
              {moment.title}
            </h1>
            <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600 max-w-3xl">
              {moment.summary}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-5 text-xs md:text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                收录于 {formatDate(moment.date)}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {formatCount(moment.views)} 观看
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-heart-3-line"></i>
                {formatCount(moment.likes)} 点赞
              </span>
            </div>
          </header>

          <div className="mt-8 w-full h-64 md:h-[420px] rounded-lg overflow-hidden bg-background-200">
            <img
              src={moment.cover}
              alt={`${moment.title} 关键画面封面`}
              title={`${moment.title} · 私拍吃瓜名场面`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="mt-8 p-6 md:p-7 rounded-lg bg-secondary-100 border border-secondary-200">
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 flex items-center justify-center rounded-md bg-secondary-500 text-foreground-950">
                <i className="ri-focus-3-line"></i>
              </span>
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950">关键画面目录</h2>
            </div>
            <p className="mt-3 text-sm text-foreground-700">
              先看这几处就够了,时间点按先后排列。
            </p>
            <ul className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
              {moment.highlights.map((item, index) => (
                <li key={item.desc} className="p-4 rounded-md bg-background-50">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-500 text-background-50 text-xs">
                      {index + 1}
                    </span>
                    <span className="font-heading text-base text-accent-700">{item.time}</span>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-700">{item.desc}</p>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_280px] gap-10">
            <div className="max-w-3xl">
              {moment.sections.map((section, index) => (
                <section key={section.heading} id={`section-${index}`} className="mb-9 scroll-mt-28">
                  <h2 className="font-heading text-2xl text-foreground-950">{section.heading}</h2>
                  {section.paragraphs.map((text) => (
                    <p key={text} className="mt-4 text-sm md:text-base leading-loose text-foreground-700">
                      {text}
                    </p>
                  ))}
                </section>
              ))}

              <div className="mt-8 flex flex-wrap gap-2">
                {moment.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/moments?q=${encodeURIComponent(tag)}`}
                    className="px-3 py-1.5 rounded-md text-xs bg-background-100 text-foreground-700 hover:text-primary-700 transition-colors cursor-pointer"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </div>

            <aside className="lg:sticky lg:top-28 self-start">
              <div className="p-5 rounded-lg border border-background-200 bg-background-50">
                <h2 className="font-heading text-lg text-foreground-950">本页目录</h2>
                <ul className="mt-4 space-y-3">
                  {moment.sections.map((section, index) => (
                    <li key={section.heading}>
                      <a
                        href={`#section-${index}`}
                        className="block text-sm text-foreground-600 hover:text-primary-700 transition-colors"
                      >
                        {section.heading}
                      </a>
                    </li>
                  ))}
                </ul>

                <div className="mt-6 pt-5 border-t border-background-200">
                  <h2 className="font-heading text-lg text-foreground-950">内容信息</h2>
                  <p className="mt-3 text-sm text-foreground-600">
                    分类:{' '}
                    <Link
                      to={`/moments?category=${encodeURIComponent(moment.category)}`}
                      className="text-primary-700 cursor-pointer"
                    >
                      {moment.category}
                    </Link>
                  </p>
                  <p className="mt-2 text-sm text-foreground-600">关键画面 {moment.highlights.length} 处</p>
                </div>
              </div>
            </aside>
          </div>
        </article>

        {related.length > 0 && (
          <section className="px-4 md:px-8 py-12 md:py-16 bg-background-100">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">同类名场面推荐</h2>
            <p className="mt-3 text-sm text-foreground-600">
              继续看「{moment.category}」分类下的其它整理。
            </p>
            <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((item) => (
                <MomentCard key={item.id} moment={item} />
              ))}
            </div>
          </section>
        )}
      </main>

      <SiteFooter />
      <ScrollToTopButton />
    </div>
  );
}